import React, { useEffect, useState, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartCrack, Sparkles, Crown, ShoppingCart, Check, Gift, ArrowDown, Star, X } from 'lucide-react';
// Purchase notification names and locations for social proof
const purchaseNotifications = [{
  name: 'María G.',
  location: 'Miami, FL'
}, {
  name: 'Carmen R.',
  location: 'Los Angeles, CA'
}, {
  name: 'Isabella M.',
  location: 'Houston, TX'
}, {
  name: 'Sofía L.',
  location: 'New York, NY'
}, {
  name: 'Valentina P.',
  location: 'Chicago, IL'
}, {
  name: 'Camila S.',
  location: 'Phoenix, AZ'
}, {
  name: 'Lucía F.',
  location: 'San Antonio, TX'
}, {
  name: 'Daniela V.',
  location: 'San Diego, CA'
}];
export function ToxicDetoxLanding() {
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(0);
  const purchaseSectionRef = useRef<HTMLElement>(null);
  const scrollToPurchase = () => {
    purchaseSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Purchase notifications
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentNotification(prev => (prev + 1) % purchaseNotifications.length);
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 4000);
    }, 7000);
    return () => clearInterval(interval);
  }, []);
  const CTAButton = ({
    className = ''
  }: {
    className?: string;
  }) => <motion.button onClick={scrollToPurchase} whileHover={{
    scale: 1.05
  }} whileTap={{
    scale: 0.95
  }} className={`bg-gradient-to-r from-rose-600 to-purple-600 text-white px-8 py-4 rounded-full text-[clamp(1.125rem,2.5vw,1.5rem)] font-bold shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`} data-id="element-4">
      Quiero Empezar Ahora
    </motion.button>;
  return <div className="min-h-screen w-full bg-gradient-to-b from-rose-50 via-purple-50 to-amber-50" data-id="element-5">
      {/* Purchase Notification Popup */}
      <AnimatePresence data-id="element-6">
        {showNotification && <motion.div initial={{
        x: -400,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -400,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 100,
        damping: 20
      }} className="fixed bottom-6 left-6 z-50 bg-white rounded-lg shadow-2xl p-4 max-w-sm border-l-4 border-green-500" data-id="element-7">
            <div className="flex items-start gap-3" data-id="element-8">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-9">
                <Check className="w-6 h-6 text-green-600" data-id="element-10" />
              </div>
              <div className="flex-1" data-id="element-11">
                <p className="font-semibold text-gray-900 text-sm" data-id="element-12">
                  {purchaseNotifications[currentNotification].name}
                </p>
                <p className="text-xs text-gray-600" data-id="element-13">
                  {purchaseNotifications[currentNotification].location}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-14">
                  Acaba de comprar Toxic Relationship Detox™
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* HERO: No llegaste aquí por casualidad */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-15">
        <div className="max-w-5xl mx-auto" data-id="element-16">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-17">
            {/* Title in Styled Box */}
            <div className="bg-purple-900 rounded-3xl px-8 py-6 mb-8 shadow-2xl" data-id="element-18">
              <h2 className="text-[clamp(1.75rem,5vw,3.5rem)] font-bold text-rose-50 text-center leading-tight" data-id="element-19">
                NO LLEGASTE AQUÍ POR CASUALIDAD
              </h2>
            </div>

            {/* New Broken Heart Image */}
            <div className="mb-8" data-id="element-20">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4mc6dzRczhK4WqenVGELuC/ChatGPT_Image_27_nov_2025%2C_12_01_49_p.m..png" alt="Sanando un corazón roto" className="w-full h-auto object-contain rounded-2xl shadow-2xl" data-id="element-21" />
            </div>

            {/* Text Content */}
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800 mb-12" data-id="element-22">
              <p data-id="element-23">
                Llegaste aquí porque tu corazón está cansado, tu cuerpo está en
                alerta y tu alma lleva tiempo susurrándote: "Ya basta".
              </p>
              <p data-id="element-24">
                Quizás ya intentaste irte. Quizás te fuiste y regresaste. Quizás
                sigues ahí, justificando: "no es para tanto, yo también tengo
                mis errores".
              </p>
              <p data-id="element-25">Pero en lo profundo sabes la verdad:</p>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-rose-700 text-center my-8" data-id="element-26">
                Esto no es amor. Es adicción emocional envuelta en culpa
                cultural.
              </p>
              <p data-id="element-27">Y ya no quieres seguir sobreviviendo en nombre del "amor".</p>
            </div>

            {/* Descubre Cómo Button */}
            <div className="flex justify-center" data-id="element-28">
              <motion.button onClick={scrollToPurchase} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-gradient-to-r from-rose-600 to-purple-600 text-white px-12 py-5 rounded-full text-[clamp(1.25rem,3vw,1.75rem)] font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center gap-3" data-id="element-29">
                Descubre Cómo
                <ArrowDown className="w-6 h-6 animate-bounce" data-id="element-30" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Title + Book Cover - UPDATED SUBTITLE */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-31">
        <div className="max-w-6xl mx-auto" data-id="element-32">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-8" data-id="element-33">
            <h1 className="font-serif text-[clamp(2rem,6vw,4rem)] leading-tight mb-4 text-purple-900" data-id="element-34">
              TOXIC RELATIONSHIP DETOX™ — LATINA EDITION
            </h1>
            <div className="text-[clamp(1.25rem,3vw,2rem)] text-rose-600 font-light mb-4" data-id="element-35">
              Programa de 90 días para mujeres latinas que desean:
            </div>
            <ul className="text-[clamp(1.125rem,2.5vw,1.5rem)] text-gray-700 space-y-2 mb-8" data-id="element-36">
              <li data-id="element-37">romper vínculos tóxicos</li>
              <li data-id="element-38">sanar heridas profundas</li>
              <li data-id="element-39">renacer emocionalmente</li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex justify-center" data-id="element-40">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3x5bBtCaSuPuiPb8bCmZhS/Generated_Image_November_17%2C_2025_-_1_26PM.png" alt="Toxic Relationship Detox Book Cover" className="w-full max-w-md h-auto object-contain shadow-2xl rounded-lg" data-id="element-41" />
          </motion.div>
        </div>
      </section>

      {/* La montaña rusa */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-42">
        <div className="max-w-4xl mx-auto" data-id="element-43">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-44">
            <div className="flex items-center justify-center mb-8" data-id="element-45">
              <HeartCrack className="w-12 h-12 text-rose-600 mr-4" data-id="element-46" />
              <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900" data-id="element-47">
                La montaña rusa que nadie ve
              </h2>
            </div>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-48">
              <p data-id="element-49">
                Te despiertas con un nudo en el estómago. Revisas tu teléfono
                antes de respirar.
              </p>
              <p data-id="element-50">
                "¿Me escribió?" "¿Por qué estuvo en línea y no contestó?"
                "¿Quién es esa nueva amiga que apareció en su perfil?"
              </p>
              <p data-id="element-51">Vives pendiente de qué versión de él te va a tocar hoy:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-52">
                <li className="text-rose-700" data-id="element-53">El amoroso</li>
                <li className="text-rose-700" data-id="element-54">El frío</li>
                <li className="text-rose-700" data-id="element-55">El desaparecido</li>
                <li className="text-rose-700" data-id="element-56">El agresivo</li>
                <li className="text-rose-700" data-id="element-57">
                  El que te castiga con silencio
                </li>
              </ul>
              <p data-id="element-58">Y siempre termina igual:</p>
              <p className="italic" data-id="element-59">
                "Estás exagerando." "Estás loca." "Eso nunca pasó."
              </p>
              <p data-id="element-60">Y poco a poco empiezas a creerle.</p>
              <p data-id="element-61">Empiezas a dudar de tu intuición, tu memoria y tu valor.</p>
              <p data-id="element-62">Tu familia te enseñó:</p>
              <p className="italic" data-id="element-63">
                "Aguanta, mija." "El amor todo lo soporta." "Mejor eso que estar
                sola."
              </p>
              <p data-id="element-64">Y tú cargas la culpa de desear paz.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Couple Arguing Image */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-65">
        <div className="max-w-5xl mx-auto" data-id="element-66">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-67">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4nT8V7HT8zeXrPtPBiV8HE/ChatGPT_Image_24_nov_2025%2C_10_41_31_p.m..png" alt="La realidad de una relación tóxica" className="w-full h-auto object-contain rounded-2xl shadow-2xl" data-id="element-68" />
          </motion.div>
        </div>
      </section>

      {/* Cultural Programming */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-69">
        <div className="max-w-4xl mx-auto" data-id="element-70">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-71">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900 mb-8 text-center" data-id="element-72">
              No es solo él. Es un programa cultural.
            </h2>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-73">
              <p data-id="element-74">Si esto fuera solo una relación, ya te hubieras ido.</p>
              <p data-id="element-75">Pero estás luchando contra algo más profundo:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-76">
                <li className="text-purple-700" data-id="element-77">
                  El machismo que viste desde niña
                </li>
                <li className="text-purple-700" data-id="element-78">El qué dirán</li>
                <li className="text-purple-700" data-id="element-79">La culpa religiosa</li>
                <li className="text-purple-700" data-id="element-80">
                  La idea de que una mujer sola "fracasó"
                </li>
                <li className="text-purple-700" data-id="element-81">
                  La presión de ser la fuerte, la que aguanta, la que calla
                </li>
              </ul>
              <p data-id="element-82">Llevas años con el mismo patrón interno:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-83">
                <li className="text-rose-700" data-id="element-84">Drama igual pasión</li>
                <li className="text-rose-700" data-id="element-85">Celos igual amor</li>
                <li className="text-rose-700" data-id="element-86">
                  Aguantar igual ser buena mujer
                </li>
                <li className="text-rose-700" data-id="element-87">
                  Perdonar sin cambios igual "trabajar en la relación"
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 text-center my-8" data-id="element-88">
                Pero lo aprendido se puede desaprender. Lo vivido se puede
                sanar. Y el ciclo se puede romper.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Nothing Worked */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-89">
        <div className="max-w-4xl mx-auto" data-id="element-90">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-91">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-serif text-purple-900 mb-8 text-center" data-id="element-92">
              Por qué nada funcionó antes
            </h2>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-93">
              <p data-id="element-94">Probablemente ya intentaste:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-95">
                <li className="text-gray-700" data-id="element-96">Terapia</li>
                <li className="text-gray-700" data-id="element-97">Videos motivacionales</li>
                <li className="text-gray-700" data-id="element-98">Hablar con amigas</li>
                <li className="text-gray-700" data-id="element-99">Journaling</li>
                <li className="text-gray-700" data-id="element-100">Oración</li>
                <li className="text-gray-700" data-id="element-101">Bloquearlo y desbloquearlo</li>
                <li className="text-gray-700" data-id="element-102">Irte y volver</li>
              </ul>
              <p data-id="element-103">Todo te ayuda un poco, pero no te libera del todo.</p>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-rose-700 text-center my-8" data-id="element-104">
                La razón es simple:
              </p>
              <p data-id="element-105">
                Ninguna de esas cosas está diseñada para mujeres latinas con
                nuestra historia emocional, cultural y espiritual.
              </p>
              <p data-id="element-106">Nuestros vínculos traumáticos se sostienen por:</p>
              <ul className="list-none space-y-3 my-8 pl-6" data-id="element-107">
                <li className="text-purple-700" data-id="element-108">Culpa</li>
                <li className="text-purple-700" data-id="element-109">Programación familiar</li>
                <li className="text-purple-700" data-id="element-110">Miedo al abandono</li>
                <li className="text-purple-700" data-id="element-111">Presión social</li>
                <li className="text-purple-700" data-id="element-112">
                  Espiritualidad mal entendida
                </li>
                <li className="text-purple-700" data-id="element-113">
                  Un sistema nervioso acostumbrado al caos
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 text-center my-8" data-id="element-114">
                No eres débil. Nadie te enseñó el método correcto.
              </p>
              <p className="text-center text-[clamp(1.5rem,3.5vw,2rem)] text-rose-600 font-semibold" data-id="element-115">
                Hasta hoy.
              </p>
            </div>

            {/* CTA Button 1 */}
            <div className="flex justify-center mt-12" data-id="element-116">
              <CTAButton data-id="element-117" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Offer */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-12" data-id="element-118">
        <div className="max-w-5xl mx-auto" data-id="element-119">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-8 sm:p-12 lg:p-16 border-4 border-rose-300" data-id="element-120">
            <div className="flex items-center justify-center mb-8" data-id="element-121">
              <Sparkles className="w-12 h-12 text-amber-500 mr-4" data-id="element-122" />
              <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-serif text-purple-900 text-center" data-id="element-123">
                Toxic Relationship Detox™ — Latina Edition
              </h2>
              <Sparkles className="w-12 h-12 text-amber-500 ml-4" data-id="element-124" />
            </div>
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-125">
              <p data-id="element-126">
                Un detox emocional profundo y transformador creado para mujeres
                que quieren:
              </p>
              <ul className="list-none space-y-4 my-8 pl-6" data-id="element-127">
                <li className="flex items-start" data-id="element-128">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-129">✓</span>
                  <span data-id="element-130">Romper vínculos traumáticos</span>
                </li>
                <li className="flex items-start" data-id="element-131">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-132">✓</span>
                  <span data-id="element-133">Sanar heridas generacionales</span>
                </li>
                <li className="flex items-start" data-id="element-134">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-135">✓</span>
                  <span data-id="element-136">Liberarse del ciclo tóxico</span>
                </li>
                <li className="flex items-start" data-id="element-137">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-138">✓</span>
                  <span data-id="element-139">Recuperar su identidad y su paz</span>
                </li>
                <li className="flex items-start" data-id="element-140">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-141">✓</span>
                  <span data-id="element-142">Reprogramar su mente y su corazón</span>
                </li>
                <li className="flex items-start" data-id="element-143">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-144">✓</span>
                  <span data-id="element-145">
                    Dejar de repetir la misma historia con diferente cara
                  </span>
                </li>
                <li className="flex items-start" data-id="element-146">
                  <span className="text-rose-600 mr-3 text-xl" data-id="element-147">✓</span>
                  <span data-id="element-148">Renacer como Reina Reborn™</span>
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 text-center my-8" data-id="element-149">
                No es teoría. No es un libro genérico. Es un proceso creado para
                tu realidad emocional y cultural.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 7 Steps */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-150">
        <div className="max-w-5xl mx-auto" data-id="element-151">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-152">
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-serif text-purple-900 mb-12 text-center" data-id="element-153">
              Tu mapa: El Sistema de Detox en 7 Pasos
            </h2>
            <div className="space-y-8" data-id="element-154">
              {[{
              number: '1',
              title: 'Reset de No Contacto',
              description: 'Cómo sostener límites firmes sin culpa ni ansiedad.'
            }, {
              number: '2',
              title: 'Depuración Digital',
              description: 'Elimina disparadores, recuerdos y accesos emocionales.'
            }, {
              number: '3',
              title: 'Liberación Emocional',
              description: 'Ejercicios guiados para soltar rabia, tristeza, decepción y vergüenza.'
            }, {
              number: '4',
              title: 'Ritual Energético de Cierre',
              description: 'Cierra el ciclo desde dentro y corta lazos energéticos.'
            }, {
              number: '5',
              title: 'Reprogramación del Subconsciente',
              description: 'Elimina creencias tóxicas y activa tu identidad sana.'
            }, {
              number: '6',
              title: 'Liberación del Trauma en el Cuerpo',
              description: 'Regula tu sistema nervioso y sana la ansiedad, hipervigilancia y tensión.'
            }, {
              number: '7',
              title: 'Renacimiento de Autoestima y Reapertura del Corazón',
              description: 'Reconéctate contigo y entra en tu era Reina Reborn™.'
            }].map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-gradient-to-r from-rose-50 to-purple-50 rounded-xl p-6 sm:p-8 shadow-lg border-l-4 border-rose-400" data-id="element-155">
                  <div className="flex items-start gap-4" data-id="element-156">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl" data-id="element-157">
                      {step.number}
                    </div>
                    <div className="flex-1" data-id="element-158">
                      <h3 className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 mb-2" data-id="element-159">
                        {step.title}
                      </h3>
                      <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 leading-relaxed" data-id="element-160">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* CTA Button 2 */}
            <div className="flex justify-center mt-12" data-id="element-161">
              <CTAButton data-id="element-162" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-163">
        <div className="max-w-4xl mx-auto" data-id="element-164">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-165">
            <div className="space-y-6 text-[clamp(1rem,2.5vw,1.25rem)] leading-relaxed text-gray-800" data-id="element-166">
              <p data-id="element-167">
                Esta guía no te promete olvidar de un día para otro. Tampoco
                pretende minimizar lo que has vivido.
              </p>
              <p data-id="element-168">
                Este proceso está diseñado para acompañarte paso a paso, con
                compasión, claridad y estructura. No se trata de obligarte a ser
                fuerte. Se trata de ayudarte a sentirte segura mientras avanzas.
              </p>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-purple-900 my-8" data-id="element-169">
                En un periodo de semanas podrás notar cambios como:
              </p>
              <ul className="list-none space-y-4 my-8 pl-6" data-id="element-170">
                <li className="flex items-start" data-id="element-171">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-172">→</span>
                  <span data-id="element-173">Pensamientos más claros</span>
                </li>
                <li className="flex items-start" data-id="element-174">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-175">→</span>
                  <span data-id="element-176">Menos necesidad de revisar el teléfono</span>
                </li>
                <li className="flex items-start" data-id="element-177">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-178">→</span>
                  <span data-id="element-179">Una sensación nueva de calma en el cuerpo</span>
                </li>
                <li className="flex items-start" data-id="element-180">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-181">→</span>
                  <span data-id="element-182">Decisiones que antes te parecían imposibles</span>
                </li>
                <li className="flex items-start" data-id="element-183">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-184">→</span>
                  <span data-id="element-185">Límites más firmes sin tanta culpa</span>
                </li>
                <li className="flex items-start" data-id="element-186">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-187">→</span>
                  <span data-id="element-188">Una relación más honesta contigo misma</span>
                </li>
                <li className="flex items-start" data-id="element-189">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-190">→</span>
                  <span data-id="element-191">
                    La capacidad de ver la situación sin idealizar ni justificar
                  </span>
                </li>
                <li className="flex items-start" data-id="element-192">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-193">→</span>
                  <span data-id="element-194">
                    Más respeto propio y menos necesidad de aprobación
                  </span>
                </li>
                <li className="flex items-start" data-id="element-195">
                  <span className="text-amber-600 mr-3 text-xl" data-id="element-196">→</span>
                  <span data-id="element-197">
                    La sensación de que estás recuperando tu identidad
                  </span>
                </li>
              </ul>
              <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-rose-700 text-center my-8" data-id="element-198">
                Y lo más importante:
              </p>
              <p data-id="element-199">
                Volverás a sentir que tienes control sobre tu vida emocional. No
                porque seas "fuerte", sino porque por fin tendrás un camino que
                sostiene tu proceso en cada capa: mental, emocional, corporal y
                cultural.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-10" data-id="element-200">
        <div className="max-w-6xl mx-auto" data-id="element-201">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-202">
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-serif text-purple-900 mb-12 text-center" data-id="element-203">
              Lo que dicen mujeres que ya hicieron el Detox
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-204">
              {[{
              name: 'Andrea, 34 años',
              text: 'Pensé que nunca iba a poder soltarlo. Regresé a él más de ocho veces. No sabía cómo cortar ese lazo, aunque sabía que me estaba destruyendo. Este detox me ayudó a entender por qué me costaba tanto y qué hacer en cada etapa. Por primera vez sentí paz y claridad. No regresé más. Recuperé mi vida.'
            }, {
              name: 'Valeria, 41 años',
              text: 'Yo no crecí viendo amor sano. Aguanté cosas que hoy no puedo creer. Este método fue como tener una mano guiándome. Lo que más me ayudó fue la parte del trauma en el cuerpo, porque yo vivía en alerta todo el tiempo. Ahora duermo mejor, como mejor y siento que volví a ser yo.'
            }, {
              name: 'Sofía, 28 años',
              text: 'Estaba tan confundida que no sabía si yo era la tóxica. Después del detox, entendí mi patrón, entendí mi herida y dejé de idealizarlo. Me tomó semanas ver cambios, pero un día desperté y ya no sentí la necesidad de buscarlo. Fue como si mi cuerpo por fin entendiera que merezco algo diferente.'
            }].map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.2
            }} className="bg-white rounded-xl p-6 sm:p-8 shadow-xl border-t-4 border-rose-400" data-id="element-205">
                  <p className="text-[clamp(1rem,2.5vw,1.125rem)] text-gray-700 leading-relaxed mb-6 italic" data-id="element-206">
                    "{testimonial.text}"
                  </p>
                  <p className="text-[clamp(1rem,2.5vw,1.125rem)] font-semibold text-purple-900" data-id="element-207">
                    Testimonio {index + 1} – {testimonial.name}
                  </p>
                </motion.div>)}
            </div>

            {/* CTA Button 3 */}
            <div className="flex justify-center mt-12" data-id="element-208">
              <CTAButton data-id="element-209" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Purchase Section */}
      <section ref={purchaseSectionRef} className="w-full px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-purple-900 via-rose-900 to-purple-900" data-id="element-210">
        <div className="max-w-6xl mx-auto" data-id="element-211">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-212">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0" data-id="element-213">
              {/* Left: Product Image */}
              <div className="bg-gradient-to-br from-rose-50 to-purple-50 p-8 sm:p-12 flex items-center justify-center" data-id="element-214">
                <motion.img initial={{
                scale: 0.9,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.2
              }} src="https://uploadthingy.s3.us-west-1.amazonaws.com/dkuGt92J5PUPup1iCyStpP/Image-FX-2025-11-24.jpg" alt="Toxic Relationship Detox - Digital Product" className="w-full h-auto object-contain max-w-lg" data-id="element-215" />
              </div>

              {/* Right: Purchase Details */}
              <div className="p-8 sm:p-12 flex flex-col justify-center" data-id="element-216">
                <div className="mb-8" data-id="element-217">
                  <h3 className="text-[clamp(1.75rem,4vw,2.5rem)] font-serif text-purple-900 mb-4" data-id="element-218">
                    Comienza Tu Transformación Hoy
                  </h3>
                  <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 leading-relaxed mb-6" data-id="element-219">
                    Acceso inmediato a la guía completa en formato digital.
                    Disponible en todos tus dispositivos.
                  </p>
                </div>

                {/* Is This For You Section */}
                <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-2xl p-6 mb-8 border-2 border-purple-300" data-id="element-220">
                  <h4 className="text-[clamp(1.25rem,3vw,1.5rem)] font-bold text-purple-900 mb-4 flex items-center gap-2" data-id="element-221">
                    🔐 ¿ESTE PROGRAMA ES PARA TI SI…?
                  </h4>
                  <ul className="space-y-2 mb-6" data-id="element-222">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-223">
                      <span className="text-green-600 mr-2" data-id="element-224">✅</span>
                      <span data-id="element-225">Te cuesta soltar aunque sabes que te duele</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-226">
                      <span className="text-green-600 mr-2" data-id="element-227">✅</span>
                      <span data-id="element-228">Te sientes cansada emocionalmente</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-229">
                      <span className="text-green-600 mr-2" data-id="element-230">✅</span>
                      <span data-id="element-231">Vives ansiosa revisando mensajes</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-232">
                      <span className="text-green-600 mr-2" data-id="element-233">✅</span>
                      <span data-id="element-234">Te cuesta poner límites</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-235">
                      <span className="text-green-600 mr-2" data-id="element-236">✅</span>
                      <span data-id="element-237">Repites el mismo patrón</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-238">
                      <span className="text-green-600 mr-2" data-id="element-239">✅</span>
                      <span data-id="element-240">Te cuesta confiar en ti misma</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-241">
                      <span className="text-green-600 mr-2" data-id="element-242">✅</span>
                      <span data-id="element-243">Quieres paz más que intensidad</span>
                    </li>
                  </ul>

                  <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-bold text-rose-700 mb-3" data-id="element-244">
                    ❌ ESTE PROGRAMA NO ES PARA TI SI:
                  </h5>
                  <ul className="space-y-2" data-id="element-245">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-246">
                      <span className="text-rose-600 mr-2" data-id="element-247">❌</span>
                      <span data-id="element-248">Quieres cambiarlo a él</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-249">
                      <span className="text-rose-600 mr-2" data-id="element-250">❌</span>
                      <span data-id="element-251">Buscas una solución mágica</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-252">
                      <span className="text-rose-600 mr-2" data-id="element-253">❌</span>
                      <span data-id="element-254">No estás lista para mirarte</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-255">
                      <span className="text-rose-600 mr-2" data-id="element-256">❌</span>
                      <span data-id="element-257">No quieres responsabilizarte de tu sanación</span>
                    </li>
                  </ul>
                </div>

                {/* Pricing */}
                <div className="mb-8 text-center" data-id="element-258">
                  <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-600 mb-2" data-id="element-259">
                    VALOR TOTAL REAL:
                  </p>
                  <p className="text-[clamp(2rem,5vw,3rem)] font-bold text-gray-400 line-through mb-2" data-id="element-260">
                    $147
                  </p>
                  <p className="text-[clamp(1.25rem,3vw,1.75rem)] text-rose-600 font-bold mb-2" data-id="element-261">
                    👉 HOY SOLO:
                  </p>
                  <div className="flex items-baseline justify-center mb-2" data-id="element-262">
                    <span className="text-[clamp(3rem,8vw,5rem)] font-bold text-purple-900" data-id="element-263">
                      $47
                    </span>
                  </div>
                  <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-600" data-id="element-264">
                    Inversión única • Acceso inmediato
                  </p>
                </div>

                {/* Bonuses Section */}
                <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-2xl p-6 mb-8 border-2 border-amber-300" data-id="element-265">
                  <h4 className="text-[clamp(1.25rem,3vw,1.5rem)] font-bold text-amber-700 mb-4 text-center" data-id="element-266">
                    🎁 BONOS INCLUIDOS (HOY SIN COSTO ADICIONAL)
                  </h4>

                  {/* Bonus 1 */}
                  <div className="mb-6" data-id="element-267">
                    <div className="flex items-center justify-center mb-3" data-id="element-268">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sq9uL7Ee1UGsQGj5XM5H85/ChatGPT_Image_26_nov_2025%2C_11_21_46_p.m..png" alt="Diario Emocional 30 Días" className="w-32 h-auto object-contain rounded-lg shadow-lg" data-id="element-269" />
                    </div>
                    <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-semibold text-purple-900 mb-2 text-center" data-id="element-270">
                      BONUS #1 – Diario Emocional 30 Días
                    </h5>
                    <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-700 leading-relaxed text-center" data-id="element-271">
                      Tu espacio diario para soltar, sanar y reprogramar con
                      claridad emocional.
                    </p>
                  </div>

                  {/* Bonus 2 */}
                  <div className="pt-4 border-t border-amber-200" data-id="element-272">
                    <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-semibold text-purple-900 mb-2 text-center" data-id="element-273">
                      BONUS #2 – Meditación Guiada: Cierre Energético +
                      Afirmaciones
                    </h5>
                    <p className="text-[clamp(0.875rem,2vw,1rem)] text-gray-700 leading-relaxed text-center" data-id="element-274">
                      Un audio para cerrar ciclos internos, calmar ansiedad y
                      fortalecer tu autoestima.
                    </p>
                  </div>
                </div>

                <motion.a href="https://www.mialmazen.com/cart/47396884742282:1" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full bg-gradient-to-r from-rose-600 to-purple-600 text-white py-6 rounded-full text-[clamp(1.25rem,3vw,1.5rem)] font-bold shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center justify-center gap-1" data-id="element-275">
                  <span className="flex items-center gap-2" data-id="element-276">🛒 Sí, quiero</span>
                  <span data-id="element-277">transformar mi vida ahora</span>
                </motion.a>

                {/* Compra con Confianza */}
                <div className="mt-8 bg-green-50 rounded-xl p-6 border-2 border-green-200" data-id="element-278">
                  <h5 className="text-[clamp(1rem,2.5vw,1.25rem)] font-bold text-green-800 mb-4 text-center" data-id="element-279">
                    🔒 COMPRA CON CONFIANZA
                  </h5>
                  <ul className="space-y-2" data-id="element-280">
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-281">
                      <span className="text-green-600 mr-2" data-id="element-282">✔️</span>
                      <span data-id="element-283">Acceso inmediato después del pago</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-284">
                      <span className="text-green-600 mr-2" data-id="element-285">✔️</span>
                      <span data-id="element-286">Pagos seguros procesados por Shopify</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-287">
                      <span className="text-green-600 mr-2" data-id="element-288">✔️</span>
                      <span data-id="element-289">Contenido privado y descargable</span>
                    </li>
                    <li className="flex items-start text-[clamp(0.875rem,2vw,1rem)] text-gray-700" data-id="element-290">
                      <span className="text-green-600 mr-2" data-id="element-291">✔️</span>
                      <span data-id="element-292">Acceso desde cualquier dispositivo</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final Security Message */}
      <section className="w-full px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-rose-50 to-purple-50" data-id="element-293">
        <div className="max-w-4xl mx-auto" data-id="element-294">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white rounded-2xl p-8 shadow-xl border-2 border-green-300 text-center" data-id="element-295">
            <h3 className="text-[clamp(1.5rem,4vw,2rem)] font-bold text-green-800 mb-4" data-id="element-296">
              ✅ COMPRA SEGURA
            </h3>
            <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-gray-700 leading-relaxed" data-id="element-297">
              Ofrecemos un proceso real que requiere compromiso contigo misma.
              Si sabes que estás lista para sanar, este programa es para ti.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}